import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

const BUCKET = "prel-media";

function prettyBytes(num = 0) {
  if (!num) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = num;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function sanitizeFolder(value = "") {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\\/g, "/")
    .replace(/[^a-z0-9/_-]/g, "-")
    .replace(/\/+/g, "/")
    .replace(/^\/+|\/+$/g, "");
}

function pick(row, headers, candidates = []) {
  const normalized = new Map(headers.map((h) => [String(h).trim().toLowerCase(), h]));
  for (const c of candidates) {
    const match = normalized.get(c.toLowerCase());
    if (match && row[match] != null && String(row[match]).trim() !== "") return String(row[match]).trim();
  }
  return "";
}

function parseStructureRows(rows) {
  if (!rows.length) return [];
  const headers = Object.keys(rows[0] || {});

  return rows
    .map((row, idx) => {
      const folder =
        pick(row, headers, ["folder", "carpeta", "directorio", "ruta", "path", "section", "categoria", "category"]) ||
        "sin-clasificar";
      const fileName = pick(row, headers, ["file", "filename", "archivo", "nombre", "asset", "title", "media"]);
      const note = pick(row, headers, ["notes", "nota", "descripcion", "description", "comentario", "comment"]);
      const sourceUrl = pick(row, headers, ["url", "source", "fuente", "link", "enlace"]);
      const mimeType = pick(row, headers, ["type", "mime", "mime_type", "formato", "format"]);
      const normalizedFolder = sanitizeFolder(folder) || "sin-clasificar";

      const issues = [];
      if (!normalizedFolder) issues.push("Carpeta vacía");
      if (!fileName && !sourceUrl) issues.push("Falta archivo o URL fuente");
      if (sourceUrl && !/^https?:\/\//i.test(sourceUrl)) issues.push("URL inválida (debe empezar con http/https)");

      return {
        id: idx + 1,
        folder: normalizedFolder,
        fileName,
        note,
        sourceUrl,
        mimeType,
        valid: issues.length === 0,
        issues,
      };
    })
    .filter((r) => r.folder || r.fileName || r.sourceUrl);
}

function fileTypeFromPath(path = "") {
  const ext = String(path).toLowerCase().split(".").pop() || "";
  if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext)) return "image";
  if (["mp4", "webm", "mov"].includes(ext)) return "video";
  if (["pdf"].includes(ext)) return "pdf";
  if (["txt", "md", "json", "csv", "xlsx", "xls"].includes(ext)) return "document";
  return "other";
}

export default function AdminMediaDirectory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [folder, setFolder] = useState("media-general");
  const [importedRows, setImportedRows] = useState([]);
  const [importName, setImportName] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const fileRef = useRef(null);
  const importRef = useRef(null);

  const canWrite = hasSupabaseConfig && supabase;

  async function loadMedia() {
    setLoading(true);
    setMsg("");
    try {
      if (!canWrite) {
        setItems([]);
        return;
      }
      const scanFolders = Array.from(new Set(["", "media-general", "eventos", "sponsors", folder]));
      const all = [];

      for (const prefix of scanFolders) {
        const { data, error } = await supabase.storage.from(BUCKET).list(prefix, {
          limit: 200,
          sortBy: { column: "created_at", order: "desc" },
        });
        if (error) throw error;

        (data || []).forEach((it) => {
          if (!it?.name || !it.metadata?.size) return;
          const path = prefix ? `${prefix}/${it.name}` : it.name;
          all.push({
            name: it.name,
            path,
            size: it.metadata?.size || 0,
            createdAt: it.created_at,
          });
        });
      }

      setItems(all);
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMedia();
  }, []);

  async function uploadSelectedFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMsg("");

    if (!canWrite) {
      setMsg("Configura Supabase para subir medios reales.");
      return;
    }

    try {
      for (const file of files) {
        const safeName = file.name.replace(/\s+/g, "-");
        const path = `${folder}/${Date.now()}-${safeName}`;
        const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
          upsert: false,
          contentType: file.type || undefined,
        });
        if (error) throw error;
      }
      setMsg(`✅ ${files.length} archivo(s) subido(s) a ${folder}/`);
      await loadMedia();
    } catch (e2) {
      setMsg(`❌ ${e2.message}`);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onImportExcel(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const normalized = parseStructureRows(rawRows);

      setImportedRows(normalized);
      setImportName(file.name);
      setMsg(`✅ Importado ${file.name}: ${normalized.length} fila(s) detectadas.`);
    } catch (err) {
      setMsg(`❌ No se pudo importar Excel/CSV: ${err.message}`);
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  }

  function downloadTemplate() {
    const sample = [
      { carpeta: "media-general", archivo: "torneo-2026-banner.png", descripcion: "Banner principal", url: "", formato: "image/png" },
      { carpeta: "eventos/prcc-2026", archivo: "agenda.pdf", descripcion: "Agenda del evento", url: "", formato: "application/pdf" },
      { carpeta: "sponsors", archivo: "", descripcion: "Logo desde proveedor", url: "https://example.com/logo.png", formato: "image/png" },
    ];

    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "directorio_medios");
    XLSX.writeFile(wb, "prel_media_template.xlsx");
  }

  async function createFolderPlaceholders() {
    if (!canWrite) {
      setMsg("Configura Supabase para crear estructura real en storage.");
      return;
    }
    if (!importedRows.length) {
      setMsg("Primero importa un Excel/CSV.");
      return;
    }

    try {
      const folders = Array.from(new Set(importedRows.filter((r) => r.valid).map((r) => r.folder).filter(Boolean)));
      for (const f of folders) {
        const path = `${f}/.keep`;
        const blob = new Blob([`PREL folder seed: ${f}`], { type: "text/plain" });
        const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: true });
        if (error) throw error;
      }
      setMsg(`✅ Estructura creada/actualizada: ${folders.length} carpeta(s).`);
      await loadMedia();
    } catch (err) {
      setMsg(`❌ ${err.message}`);
    }
  }

  async function openPrivatePreview(path) {
    if (!canWrite) return;
    setPreviewLoading(true);
    setPreviewRows([]);
    const type = fileTypeFromPath(path);

    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 10);
    if (error || !data?.signedUrl) {
      setPreviewLoading(false);
      setMsg(`❌ No se pudo abrir preview: ${error?.message || "error"}`);
      return;
    }

    const nextPreview = { path, url: data.signedUrl, type };
    setPreview(nextPreview);

    try {
      if (path.toLowerCase().endsWith(".csv")) {
        const txt = await fetch(data.signedUrl).then((r) => r.text());
        const wb = XLSX.read(txt, { type: "string" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }).slice(0, 30);
        setPreviewRows(rows);
      } else if (path.toLowerCase().endsWith(".xlsx") || path.toLowerCase().endsWith(".xls")) {
        const arr = await fetch(data.signedUrl).then((r) => r.arrayBuffer());
        const wb = XLSX.read(arr, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }).slice(0, 30);
        setPreviewRows(rows);
      } else if (path.toLowerCase().endsWith(".txt") || path.toLowerCase().endsWith(".md") || path.toLowerCase().endsWith(".json")) {
        const txt = await fetch(data.signedUrl).then((r) => r.text());
        setPreviewRows([{ __text: txt.slice(0, 4000) }]);
      }
    } catch (e) {
      setMsg(`⚠️ Preview parcial: ${e.message}`);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function copyPrivateUrl(path) {
    if (!canWrite) return;
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 10);
    if (error || !data?.signedUrl) {
      setMsg(`❌ No se pudo generar enlace temporal: ${error?.message || "error"}`);
      return;
    }
    await navigator.clipboard?.writeText(data.signedUrl);
    setMsg("✅ Enlace temporal (10 min) copiado.");
  }

  async function deleteMedia(path) {
    if (!canWrite) return;
    const ok = window.confirm(`¿Borrar este archivo?\n\n${path}`);
    if (!ok) return;

    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) {
      setMsg(`❌ No se pudo borrar: ${error.message}`);
      return;
    }

    setMsg("✅ Archivo borrado.");
    await loadMedia();
  }

  const stats = useMemo(() => ({
    total: items.length,
    totalSize: items.reduce((acc, it) => acc + (it.size || 0), 0),
  }), [items]);

  const importStats = useMemo(() => {
    const folders = Array.from(new Set(importedRows.map((r) => r.folder).filter(Boolean)));
    const valid = importedRows.filter((r) => r.valid).length;
    const invalid = importedRows.length - valid;
    return { rows: importedRows.length, folders, valid, invalid };
  }, [importedRows]);

  const categories = useMemo(() => {
    return Array.from(new Set(items.map((it) => it.path.split("/")[0]).filter(Boolean))).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((it) => {
      const category = it.path.split("/")[0] || "sin-categoria";
      const matchesCategory = activeCategory === "all" || category === activeCategory;
      const haystack = `${it.name} ${it.path}`.toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [items, searchTerm, activeCategory]);

  const sortedItems = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredItems].sort((a, b) => {
      const aCat = a.path.split("/")[0] || "sin-categoria";
      const bCat = b.path.split("/")[0] || "sin-categoria";
      if (sortBy === "size") return ((a.size || 0) - (b.size || 0)) * dir;
      if (sortBy === "category") return aCat.localeCompare(bCat) * dir;
      if (sortBy === "path") return String(a.path || "").localeCompare(String(b.path || "")) * dir;
      return String(a.name || "").localeCompare(String(b.name || "")) * dir;
    });
  }, [filteredItems, sortBy, sortDir]);

  function onSort(column) {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  }

  return (
    <div className="min-h-screen bg-[#06070A] text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Admin · Directorio de Medios PREL</h1>
            <p className="text-slate-300 mt-1">Directorio interno del equipo (bucket privado), con previews y enlaces firmados temporales.</p>
          </div>
          <div className="flex gap-2">
            <a href="/admin/dashboard" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5">Dashboard</a>
            <a href="/admin/blog" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5">Admin Blog</a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/10 bg-[#111722] p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide">Bucket</div>
            <div className="text-2xl font-bold mt-2">{BUCKET}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#111722] p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide">Archivos</div>
            <div className="text-2xl font-bold mt-2">{stats.total}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#111722] p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide">Uso estimado</div>
            <div className="text-2xl font-bold mt-2">{prettyBytes(stats.totalSize)}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-violet-400/20 bg-violet-500/10 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-semibold">Planificar estructura del directorio (Excel/CSV)</h2>
            <div className="flex gap-2">
              <button type="button" onClick={downloadTemplate} className="text-sm px-3 py-1.5 rounded border border-white/20 hover:bg-white/5">Descargar plantilla</button>
              <button type="button" onClick={() => importRef.current?.click()} className="text-sm px-3 py-1.5 rounded border border-violet-300/30 text-violet-200 hover:bg-violet-400/10">Cargar archivo</button>
              <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onImportExcel} />
            </div>
          </div>
          <p className="text-xs text-slate-300">Esta sección organiza carpetas (no sube archivos físicos). Soporta encabezados variables: carpeta/folder/path, archivo/file, nota/description, url/link.</p>
          {importName ? <p className="text-sm text-slate-100">Archivo: <strong>{importName}</strong> · {importStats.rows} filas</p> : null}
          {!!importStats.rows && (
            <div className="text-xs text-slate-200 flex gap-4">
              <span>✅ Válidas: <strong>{importStats.valid}</strong></span>
              <span>⚠️ Con problemas: <strong>{importStats.invalid}</strong></span>
            </div>
          )}
          {!!importStats.folders.length && (
            <div className="text-xs text-slate-300">
              Carpetas detectadas: {importStats.folders.map((f) => <code key={f} className="mr-2">{f}/</code>)}
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={createFolderPlaceholders} className="text-sm px-3 py-1.5 rounded border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10">Crear carpetas base en storage</button>
          </div>
          {!!importedRows.length && (
            <div className="max-h-52 overflow-auto rounded border border-white/10">
              <table className="w-full text-xs">
                <thead className="bg-[#0b1019] text-slate-300">
                  <tr>
                    <th className="text-left px-2 py-1">#</th>
                    <th className="text-left px-2 py-1">Carpeta</th>
                    <th className="text-left px-2 py-1">Archivo</th>
                    <th className="text-left px-2 py-1">Tipo</th>
                    <th className="text-left px-2 py-1">Nota</th>
                    <th className="text-left px-2 py-1">Validación</th>
                  </tr>
                </thead>
                <tbody>
                  {importedRows.slice(0, 100).map((r) => (
                    <tr key={r.id} className={`border-t border-white/5 ${r.valid ? "bg-emerald-500/5" : "bg-rose-500/10"}`}>
                      <td className="px-2 py-1 text-slate-400">{r.id}</td>
                      <td className="px-2 py-1"><code>{r.folder}</code></td>
                      <td className="px-2 py-1">{r.fileName || "—"}</td>
                      <td className="px-2 py-1">{r.mimeType || "—"}</td>
                      <td className="px-2 py-1 text-slate-300">{r.note || r.sourceUrl || "—"}</td>
                      <td className="px-2 py-1 text-xs">{r.valid ? "✅ OK" : `⚠️ ${r.issues.join(" · ")}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111722] p-4 space-y-3">
          <h2 className="font-semibold">Añadir archivos al directorio</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              value={folder}
              onChange={(e) => setFolder(e.target.value.trim() || "media-general")}
              className="md:col-span-1 rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
              placeholder="Carpeta (ej. media-general, eventos, sponsors)"
            />
            <input
              ref={fileRef}
              type="file"
              multiple
              onChange={uploadSelectedFiles}
              className="md:col-span-3 rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
            />
          </div>
          <p className="text-xs text-slate-400">Tip: usa carpetas tipo <code>media-general/</code>, <code>eventos/</code>, <code>sponsors/</code> para mantener orden.</p>
          {msg ? <p className="text-sm text-slate-200">{msg}</p> : null}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111722] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Directorio de medios</h2>
            <button onClick={loadMedia} className="text-sm text-cyan-300 hover:underline">Refrescar</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o ruta..."
              className="md:col-span-2 rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
            />
            <select
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
              className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
            >
              <option value="all">Todas las categorías</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {loading ? <p className="text-slate-300">Cargando...</p> : null}
          {!loading && !items.length ? <p className="text-slate-400 text-sm">No hay archivos todavía.</p> : null}
          {!loading && !!items.length && !filteredItems.length ? <p className="text-slate-400 text-sm">No hay resultados para ese filtro.</p> : null}

          {!loading && !!filteredItems.length ? (
            <div className="rounded-xl border border-white/10 bg-[#0b1019] p-3">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-100">Media Directory (vista tipo tabla)</p>
                <p className="text-xs text-slate-400">{sortedItems.length} resultado(s)</p>
              </div>
              <div className="overflow-auto max-h-[520px] rounded-lg border border-white/10">
                <table className="w-full text-sm min-w-[980px]">
                  <thead className="bg-[#111722] sticky top-0 z-10">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input type="checkbox" disabled className="accent-cyan-400" aria-label="select-all" />
                      </th>
                      <th className="text-left px-3 py-2 text-slate-300 cursor-pointer" onClick={() => onSort("name")}>Archivo {sortBy === "name" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</th>
                      <th className="text-left px-3 py-2 text-slate-300 cursor-pointer" onClick={() => onSort("path")}>Ruta {sortBy === "path" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</th>
                      <th className="text-left px-3 py-2 text-slate-300 cursor-pointer" onClick={() => onSort("category")}>Categoría {sortBy === "category" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</th>
                      <th className="text-left px-3 py-2 text-slate-300 cursor-pointer" onClick={() => onSort("size")}>Tamaño {sortBy === "size" ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</th>
                      <th className="text-left px-3 py-2 text-slate-300">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((it, idx) => {
                      const category = it.path.split("/")[0] || "sin-categoria";
                      return (
                        <tr key={it.path} className={`border-t border-white/5 align-top ${idx % 2 ? "bg-white/[0.02]" : ""}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" disabled className="accent-cyan-400" aria-label={`row-${idx}`} />
                          </td>
                          <td className="px-3 py-2 text-slate-100 font-medium">{it.name}</td>
                          <td className="px-3 py-2 text-slate-400"><code>{it.path}</code></td>
                          <td className="px-3 py-2">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-cyan-400/15 text-cyan-200 border border-cyan-300/20">
                              {category}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{prettyBytes(it.size)}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openPrivatePreview(it.path)}
                                className="text-xs px-2.5 py-1.5 rounded border border-white/20 hover:bg-white/5"
                              >
                                Ver
                              </button>
                              <button
                                type="button"
                                onClick={() => copyPrivateUrl(it.path)}
                                className="text-xs px-2.5 py-1.5 rounded border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10"
                              >
                                Copiar enlace
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteMedia(it.path)}
                                className="text-xs px-2.5 py-1.5 rounded border border-rose-300/40 text-rose-200 hover:bg-rose-500/10"
                              >
                                Borrar
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {preview ? (
            <div className="mt-4 rounded-xl border border-cyan-300/20 bg-[#0b1019] p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-cyan-200">Directorio de medios · Vista previa: <code>{preview.path}</code></p>
                <button className="text-xs px-2 py-1 rounded border border-white/20" onClick={() => setPreview(null)}>Cerrar</button>
              </div>

              {previewLoading ? <p className="text-sm text-slate-300">Cargando preview...</p> : null}

              {!previewLoading && preview.type === "image" ? (
                <img src={preview.url} alt={preview.path} className="max-h-[420px] rounded border border-white/10" />
              ) : null}

              {!previewLoading && preview.type === "video" ? (
                <video src={preview.url} controls className="w-full max-h-[420px] rounded border border-white/10" />
              ) : null}

              {!previewLoading && preview.type === "pdf" ? (
                <iframe title="pdf-preview" src={preview.url} className="w-full h-[520px] rounded border border-white/10" />
              ) : null}

              {!previewLoading && previewRows.length && previewRows[0]?.__text ? (
                <pre className="max-h-[420px] overflow-auto text-xs bg-black/30 p-3 rounded border border-white/10">{previewRows[0].__text}</pre>
              ) : null}

              {!previewLoading && previewRows.length && !previewRows[0]?.__text ? (
                <div className="max-h-[420px] overflow-auto rounded border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-[#111722]">
                      <tr>
                        {Object.keys(previewRows[0] || {}).slice(0, 8).map((k) => (
                          <th key={k} className="text-left px-2 py-1 text-slate-300">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.slice(0, 20).map((r, i) => (
                        <tr key={i} className="border-t border-white/5">
                          {Object.keys(previewRows[0] || {}).slice(0, 8).map((k) => (
                            <td key={k} className="px-2 py-1 text-slate-200">{String(r[k] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {!previewLoading && preview.type === "other" ? (
                <a href={preview.url} target="_blank" rel="noreferrer" className="text-sm text-cyan-300 hover:underline">Abrir archivo en nueva pestaña</a>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
